import { Icon, IconsEnum } from 'components/Icons';
import styles from './LiveFiltersByGroup.module.scss';
import classNames from 'classnames';
import { DynamicImage, enLoading } from 'components/Image';
import useWindowSize from 'hooks/useWindowSize';
import { useCallback, useRef, useState } from 'react';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { resetGroupFilter, setLiveFilters } from 'features/sport/sportSlice';
import { shallowEqual } from 'fast-equals';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { isServerSide } from 'utility/constant';
import { useIsomorphicLayoutEffect } from 'hooks';

export const LiveFiltersByGroup = () => {
  const dispatch = useAppDispatch();
  const { isMobile, width } = useWindowSize();

  const containerRef = useRef<HTMLDivElement>(null);

  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);

  const resetGroupFilterLabel = useGetLabelByKey('prematch-filters-schedule-default');

  const { selectedFilters, filtersByGroup } = useTypedSelector(({ sport }) => {
    const { templateLive, selectedFiltersLive } = sport ?? {};
    return {
      templateLive: templateLive,
      filtersByGroup: templateLive?.filtersByGroup,
      selectedFilters: selectedFiltersLive,
    };
  }, shallowEqual);

  const handleGroupFilterChange = (idGruppo: number) => {
    dispatch(setLiveFilters({ idGruppo }));
  };

  const handleResetGroupFilter = () => {
    dispatch(resetGroupFilter());
  };

  const onScrollHandler = useCallback(() => {
    if (!containerRef.current) return;

    const currentPosition = containerRef.current.scrollLeft;

    const isStart = currentPosition === 0;
    setIsAtStart(isStart);

    const isEnd = currentPosition === containerRef.current.scrollWidth - containerRef.current.clientWidth;
    setIsAtEnd(isEnd);
  }, []);

  const onResizeHandler = useCallback(() => {
    if (!containerRef.current) return;

    const nextIsScrollable = containerRef.current.scrollWidth > containerRef.current.clientWidth;
    setIsScrollable(nextIsScrollable);

    onScrollHandler();
  }, [width, onScrollHandler]);

  useIsomorphicLayoutEffect(() => {
    if (isServerSide) return;

    onResizeHandler();
  }, [onResizeHandler]);

  const goBack = (refElement: HTMLDivElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft -= delta - 250;
    }
  };

  const goOn = (refElement: HTMLDivElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft += delta - 250;
    }
  };

  return (
    <div className={styles.filtersByGroupWrapper}>
      {!isMobile && isScrollable && !isAtStart && (
        <button
          className={classNames(styles.buttonScroll, styles.buttonLeft)}
          onClick={() => goBack(containerRef.current)}
        >
          <Icon iconId={IconsEnum.CHEWRON_LEFT} className={styles.icon} color="var(--color-on-bg-primary)" />
        </button>
      )}
      <button
        className={classNames(styles.groupButton, styles.fixedButton, {
          [styles.activeGroupButton]: !selectedFilters.idGruppo,
        })}
        onClick={handleResetGroupFilter}
      >
        <span className={styles.labelGroupButton}>{resetGroupFilterLabel}</span>
      </button>
      <div ref={containerRef} className={styles.containerButtons} onScroll={onScrollHandler}>
        {filtersByGroup?.map((group, index) => (
          <button
            key={`${group.descrizioneTrKey}-${group.idGruppo}-${index}`}
            className={classNames(styles.groupButton, {
              [styles.activeGroupButton]: selectedFilters.idGruppo === group.idGruppo,
            })}
            onClick={() => handleGroupFilterChange(group.idGruppo)}
          >
            <DynamicImage
              className={styles.iconCountry}
              src={group.iconSrc}
              alt={group.descrizione}
              width={16}
              height={16}
              loading={enLoading.lazy}
              decoding="async"
            />
            <span className={styles.labelGroupButton}>{group.descrizione}</span>
          </button>
        ))}
      </div>
      {!isMobile && isScrollable && !isAtEnd && (
        <button
          className={classNames(styles.buttonScroll, styles.buttonRight)}
          onClick={() => goOn(containerRef.current)}
        >
          <Icon iconId={IconsEnum.CHEWRON_RIGHT} className={styles.icon} color="var(--color-on-bg-primary)" />
        </button>
      )}
    </div>
  );
};
